import { posApi } from '@/services/api';
import { useSyncStore } from '@/store/syncStore';
import { isLocalFirstMode, companyDb } from './companyContext';
import {
  completeLocalSale,
  getLocalReceiptData,
  getLocalItemMinRate,
  listLocalCustomers,
  listLocalCustomersWithStats,
  listLocalCustomerInvoices,
  getLocalCustomerPartyLedger,
  getLocalSupplierPartyLedger,
  listLocalExpenses,
  listLocalCashPockets,
  listLocalBankAccounts,
  listLocalItems,
  listLocalSuppliers,
  saveLocalCustomer,
  saveLocalExpense,
  saveLocalItem,
  saveLocalPurchase,
  saveLocalReturn,
  saveLocalSupplier,
  adjustLocalStock,
  findLocalInvoiceForReturn,
  scanLocalBarcode,
  type CompleteSaleInput,
} from './repositories';
import type { CatalogCustomer, CatalogItem } from './db';

export async function completeSale(input: CompleteSaleInput): Promise<{ name: string; offline?: boolean }> {
  // Local-first modes ALWAYS write locally (even if navigator.onLine is still true but API is dead).
  // Also force local when browser/server report offline — axios has no path that must succeed.
  const offline =
    !navigator.onLine || !useSyncStore.getState().isServerReachable;
  if (isLocalFirstMode() || offline) {
    return completeLocalSale(input);
  }
  const res = await posApi.createInvoice({
    invoice: {
      customer: input.customer,
      items: input.items,
      discount_amount: input.discount_amount,
      tax_amount: input.tax_amount,
      write_off_amount: input.write_off_amount,
      payments: input.payments,
      notes: input.notes,
      ...(input.manager_approver ? { manager_approver: input.manager_approver } : {}),
      ...(input.sales_person ? { sales_person: input.sales_person } : {}),
      ...(input.pos_shift ? { pos_shift: input.pos_shift } : {}),
      ...(input.invoice_date ? { invoice_date: input.invoice_date } : {}),
      ...(input.previous_outstanding !== undefined ? { previous_outstanding: input.previous_outstanding } : {}),
    },
  });
  return { name: res.data.message.name };
}

export async function getItemsForPos(params?: {
  search?: string;
  item_type?: string;
  limit?: number;
}): Promise<CatalogItem[]> {
  if (isLocalFirstMode()) {
    return listLocalItems({ search: params?.search, item_type: params?.item_type });
  }
  const res = await posApi.getItems(params);
  return res.data.message || [];
}

export async function getCustomersForPos(params?: { search?: string; limit?: number }): Promise<CatalogCustomer[]> {
  if (isLocalFirstMode()) {
    return listLocalCustomers(params?.search);
  }
  const res = await posApi.getCustomers(params);
  return res.data.message || [];
}

export async function createCustomerRouted(data: Record<string, unknown>) {
  if (isLocalFirstMode()) {
    return saveLocalCustomer(
      {
        customer_name: String(data.customer_name || ''),
        mobile: data.mobile as string | undefined,
        email: data.email as string | undefined,
        city: data.city as string | undefined,
        address: data.address as string | undefined,
        customer_type: data.customer_type as string | undefined,
        is_default: data.is_default as number | undefined,
        name: data.name as string | undefined,
      },
      Boolean(data.name) || data._action === 'update',
    );
  }
  const res = await posApi.createCustomer(data);
  return res.data.message;
}

export async function createItemRouted(data: Record<string, unknown>) {
  if (isLocalFirstMode()) {
    return saveLocalItem(
      {
        ...(data as Partial<CatalogItem>),
        item_name: String(data.item_name || ''),
        item_type: (data.item_type as 'Product' | 'Service') || 'Product',
      },
      Boolean(data.name) || data._action === 'update',
    );
  }
  const res = await posApi.createItem(data);
  return res.data.message;
}

export async function createSupplierRouted(data: Record<string, unknown>) {
  if (isLocalFirstMode()) {
    return saveLocalSupplier(
      {
        supplier_name: String(data.supplier_name || ''),
        mobile: data.mobile as string | undefined,
        email: data.email as string | undefined,
        city: data.city as string | undefined,
        address: data.address as string | undefined,
        name: data.name as string | undefined,
      },
      Boolean(data.name),
    );
  }
  const res = await posApi.createSupplier(data);
  return res.data.message;
}

export async function createExpenseRouted(data: Record<string, unknown>) {
  if (isLocalFirstMode()) {
    const id = await saveLocalExpense({
      amount: Number(data.amount) || 0,
      category: data.category as string | undefined,
      payment_method: data.payment_method as string | undefined,
      cash_pocket: data.cash_pocket as string | undefined,
      bank_account: data.bank_account as string | undefined,
      remarks: data.remarks as string | undefined,
      posting_date: data.posting_date as string | undefined,
      expense_employee: data.expense_employee as string | undefined,
    });
    return { name: id, offline: true };
  }
  const res = await posApi.createPOSExpense(data);
  return res.data.message;
}

export async function createPurchaseRouted(data: Record<string, unknown>) {
  const items = (data.items as Array<Record<string, unknown>>) || [];
  const createStock = Number(data.create_stock_entry ?? 1) === 1;
  const needsIdentity = createStock && items.some(
    (it) => it.track_serials || it.track_batches || (Array.isArray(it.serials) && it.serials.length > 0) || it.batch_no,
  );
  if (needsIdentity && isLocalFirstMode()) {
    throw new Error(
      'Batch / IMEI purchase requires an online connection. Switch to Online mode and try again.',
    );
  }
  if (isLocalFirstMode()) {
    const id = await saveLocalPurchase({
      supplier: String(data.supplier || ''),
      supplierName: data.supplierName as string | undefined,
      items,
      discount_amount: Number(data.discount_amount) || 0,
      tax_amount: Number(data.tax_amount) || 0,
      payments: (data.payments as Array<Record<string, unknown>>) || [],
      notes: data.notes as string | undefined,
      net_total: data.net_total as number | undefined,
      create_stock_entry: data.create_stock_entry as number | undefined,
    });
    return { name: id, offline: true };
  }
  const res = await posApi.createPurchaseInvoice(data);
  return { name: res.data.message };
}

export async function createStockRouted(
  type: 'in' | 'out' | 'adjust',
  data: Record<string, unknown>,
) {
  if (isLocalFirstMode()) {
    const map = { in: 'stock_in', out: 'stock_out', adjust: 'stock_adjust' } as const;
    return adjustLocalStock({
      type: map[type],
      item: String(data.item || ''),
      variant: data.variant as string | undefined,
      quantity: Number(data.quantity) || 0,
      unit_price: Number(data.unit_price) || Number(data.unit_purchase_price) || 0,
      supplier: data.supplier as string | undefined,
      reason: (data.stock_out || data.reason) as string | undefined,
      transaction_date: data.transaction_date as string | undefined,
    });
  }
  if (type === 'in') {
    const res = await posApi.createStockIn(data);
    return res.data.message;
  }
  const res = await posApi.createStockOut(data);
  return res.data.message;
}

export async function createReturnRouted(data: Record<string, unknown>) {
  if (isLocalFirstMode()) {
    return saveLocalReturn({
      pos_invoice: String(data.pos_invoice || ''),
      items: (data.items as Array<Record<string, unknown>>) || [],
      return_method: data.return_method as string | undefined,
      return_bank: data.return_bank as string | undefined,
      pos_shift: data.pos_shift as string | undefined,
    });
  }
  const res = await posApi.createSalesReturn(data as {
    pos_invoice: string;
    pos_shift?: string;
    items: { invoice_item: string; quantity: number }[];
    return_method?: string;
    return_bank?: string;
  });
  return res.data.message;
}

export async function getInvoiceForReturnRouted(invoice: string) {
  if (isLocalFirstMode()) {
    const local = await findLocalInvoiceForReturn(invoice);
    if (local) return local;
    // Fall through to server if online
    if (!useSyncStore.getState().isServerReachable) {
      throw new Error('Invoice not found in local data');
    }
  }
  const res = await posApi.getInvoiceForReturn(invoice);
  return res.data.message;
}

export async function scanBarcodeRouted(code: string) {
  if (isLocalFirstMode()) {
    const item = await scanLocalBarcode(code);
    if (item) return { item, source: 'local' };
    if (!useSyncStore.getState().isServerReachable) return null;
  }
  const res = await posApi.scanBarcode(code);
  return res.data.message;
}

export async function getItemMinRateRouted(item: string, variant?: string): Promise<number> {
  if (isLocalFirstMode()) {
    const local = await getLocalItemMinRate(item, variant);
    // Prefer local catalog when offline; still try server briefly when reachable.
    if (!useSyncStore.getState().isServerReachable) return local;
    try {
      const res = await posApi.getItemMinRate(item, variant);
      return Number(res.data.message?.min_rate) || local;
    } catch {
      return local;
    }
  }
  const res = await posApi.getItemMinRate(item, variant);
  return Number(res.data.message?.min_rate) || 0;
}

/** Prefer local IndexedDB for offline invoice numbers; fall back to server when synced. */
export async function getInvoiceDetailsRouted(name: string) {
  const isOfflineNo = /^OFF-/i.test(name) || name.startsWith('local-');
  if (isLocalFirstMode() || isOfflineNo) {
    const local = await getLocalReceiptData(name);
    if (local) return local;
    // Synced sale may already have a server name; try server only if reachable
    if (!useSyncStore.getState().isServerReachable) {
      throw new Error('Invoice not found in local store');
    }
  }
  const res = await posApi.getInvoiceDetails(name);
  return res.data.message;
}

export async function createQuotationRouted(data: Record<string, unknown>) {
  if (isLocalFirstMode()) {
    const { QUEUE_PRIORITY } = await import('@/offline/db');
    const q = data as any;
    await companyDb().local_quotations.put(q);
    await companyDb().sync_queue.put({
      clientOfflineId: q.client_offline_id,
      type: 'quotation',
      status: 'pending',
      payload: q,
      createdAt: new Date().toISOString(),
      retryCount: 0,
      priority: QUEUE_PRIORITY.quotation,
    });
    return { name: q.client_offline_id, offline: true };
  }
  const res = await posApi.createQuotation(data);
  return { name: res.data?.message || res.data };
}

export { listLocalItems, listLocalCustomers, listLocalCustomersWithStats, listLocalCustomerInvoices, getLocalCustomerPartyLedger, getLocalSupplierPartyLedger, listLocalSuppliers, listLocalExpenses, listLocalCashPockets, listLocalBankAccounts, getLocalReceiptData, getLocalItemMinRate };
